import React from "react";
import FoodCalorieDatabase from "../FoodCalorieDatabase/FoodCalorieDatabase";
import "./CalorieContent.css";

export default function CalorieContent() {
  return (
    <div className="calorie-content">
      <section>
        <h2>Understanding Calories and Their Role in Your Body</h2>
        <p>
          Calories are a measure of energy. They represent the energy your body
          needs to function and the energy provided by the foods and beverages
          you consume.
        </p>

        <h3>What are Calories?</h3>
        <p>
          A calorie is a unit of energy. Your body uses calories to perform
          essential functions such as breathing, circulating blood, and
          maintaining body temperature, as well as physical activities like
          walking, running, and lifting weights.
        </p>

        <h3>Calories and Energy Balance</h3>
        <p>
          Energy balance is key to managing your weight and achieving your
          fitness goals. Here's how it works:
        </p>
        <ul>
          <li>
            <strong>Calorie Surplus:</strong> Consuming more calories than your
            body burns leads to weight gain, as the excess calories are stored
            as fat.
          </li>
          <li>
            <strong>Calorie Deficit:</strong> Consuming fewer calories than your
            body burns leads to weight loss, as your body uses stored fat for
            energy.
          </li>
          <li>
            <strong>Maintenance Calories:</strong> Consuming the same number of
            calories your body burns helps maintain your current weight.
          </li>
        </ul>

        <h3>How Activity Levels Impact Calorie Needs</h3>
        <p>
          Your calorie needs depend on factors like age, gender, weight, and
          activity level. Here’s a breakdown:
        </p>
        <ul>
          <li>
            <strong>Sedentary:</strong> Minimal physical activity, such as desk
            jobs, typically requires fewer calories.
          </li>
          <li>
            <strong>Moderately Active:</strong> Includes activities like walking
            or light exercise a few times per week.
          </li>
          <li>
            <strong>Very Active:</strong> Includes daily exercise, heavy
            physical labor, or intense training sessions.
          </li>
        </ul>

        <h3>Calorie Distribution</h3>
        <p>
          Distributing your calorie intake throughout the day can help you stay
          energized and support your goals. Aim for balanced meals that combine
          protein, carbs, and healthy fats.
        </p>
      </section>

      <section>
        <h3>2.1. Calories for Weight Loss</h3>
        <p>
          Losing weight requires creating a calorie deficit, meaning you consume
          fewer calories than your body burns. Achieving this can be done
          through a combination of adjusting your diet and increasing physical
          activity. The key is finding a balance that works for your lifestyle,
          so you don’t feel deprived or overly restricted. Here are some
          detailed tips to help you reduce your calorie intake while maintaining
          energy and avoiding feelings of hunger:
        </p>
        <ul>
          <li>
            <strong>Portion Control:</strong> Pay attention to serving sizes.
            Using smaller plates or measuring food can help prevent overeating.
            This is crucial when dining out, as restaurant portions tend to be
            larger than needed.
          </li>
          <li>
            <strong>Choose Nutrient-Dense Foods:</strong> Opt for foods that
            provide high nutritional value relative to their calorie content.
            Focus on whole foods such as fruits, vegetables, lean proteins, and
            whole grains, which not only nourish your body but also fill you up
            with fewer calories.
          </li>
          <li>
            <strong>Limit Empty Calories:</strong> Reduce consumption of sugary
            snacks, fast food, and sugary beverages. These provide little
            nutritional value and can lead to quick spikes in blood sugar,
            followed by crashes that leave you feeling hungry sooner. Instead,
            choose foods that have a low glycemic index to maintain energy
            levels.
          </li>
          <li>
            <strong>Increase Fiber Intake:</strong> High-fiber foods, like
            vegetables, beans, and oats, take longer to digest and help you feel
            fuller for longer periods. This can naturally reduce your calorie
            intake without making you feel deprived. Fiber also supports
            digestive health, which is important during weight loss.
          </li>
          <li>
            <strong>Stay Hydrated:</strong> Drinking plenty of water throughout
            the day can help control hunger and reduce calorie intake.
            Sometimes, feelings of hunger are actually signs of dehydration.
            Drinking water before meals can also reduce overall calorie
            consumption by promoting a feeling of fullness.
          </li>
          <li>
            <strong>Mindful Eating:</strong> Take your time to eat and focus on
            the flavors, textures, and sensations of your food. Eating slowly
            helps your brain recognize when you are full, reducing the
            likelihood of overeating. Avoid distractions like television or
            smartphones while eating.
          </li>
          <li>
            <strong>Meal Planning and Prepping:</strong> Plan your meals in
            advance to avoid impulsive eating. Preparing meals ahead of time
            ensures you have control over ingredients and portion sizes, helping
            you stick to your calorie goals.
          </li>
          <li>
            <strong>Healthy Snacking:</strong> If you need a snack between
            meals, choose healthy options like fruits, vegetables, nuts, or
            yogurt. These can satisfy your hunger while providing beneficial
            nutrients. Avoid high-calorie snacks that provide little satiety or
            nutrition.
          </li>
          <li>
            <strong>Get Enough Sleep:</strong> Poor sleep can affect your hunger
            hormones, leading to increased cravings for high-calorie foods. Aim
            for 7-9 hours of sleep each night to support your weight loss goals
            and improve overall health.
          </li>
          <li>
            <strong>Exercise Regularly:</strong> Engaging in regular physical
            activity, particularly cardio exercises, can help you burn more
            calories and create a larger calorie deficit. Incorporating strength
            training will help preserve lean muscle mass, ensuring that your
            body burns fat more efficiently.
          </li>
        </ul>
      </section>

      <section>
        <h3>2.2. Calories for Muscle Gain</h3>
        <p>
          To build muscle, you need a calorie surplus, meaning you consume more
          calories than your body burns. However, it’s important to ensure that
          the extra calories come from nutrient-dense foods to promote muscle
          growth rather than fat accumulation. Here are strategies to increase
          your calorie intake effectively while supporting your muscle building
          goals:
        </p>
        <ul>
          <li>
            <strong>Eat Frequently:</strong> Having 5-6 smaller meals throughout
            the day helps you meet your calorie goals without feeling overly
            full or uncomfortable. Eating frequently also provides your body
            with a steady supply of nutrients to fuel muscle repair and growth.
          </li>
          <li>
            <strong>Focus on Quality Calories:</strong> Incorporate
            calorie-dense yet healthy foods like nuts, avocados, olive oil, and
            whole grains. These foods provide a higher number of calories per
            serving while also offering essential fats, vitamins, and minerals
            to support overall health and muscle development.
          </li>
          <li>
            <strong>Track Your Progress:</strong> Monitoring your weight,
            strength, and muscle growth is crucial to ensuring you’re consuming
            enough calories. If you’re not seeing the desired results, you may
            need to adjust your calorie intake or exercise routine. Regular
            tracking helps you make informed decisions and fine-tune your
            approach.
          </li>
          <li>
            <strong>Protein is Key:</strong> Protein plays a vital role in
            muscle repair and growth. Aim to include a high-quality source of
            protein, such as chicken, fish, tofu, or beans, in every meal.
            Ideally, you should aim for 1.6–2.2 grams of protein per kilogram of
            body weight, depending on your goals and activity level.
          </li>
          <li>
            <strong>Incorporate Healthy Fats:</strong> Fats are crucial for
            hormone production, including testosterone, which plays a key role
            in muscle building. Healthy fats from sources like olive oil, fatty
            fish, and nuts should make up a significant portion of your diet.
            These fats are calorie-dense and provide slow-burning energy for
            your workouts.
          </li>
          <li>
            <strong>Choose Whole Grains:</strong> Whole grains like brown rice,
            quinoa, oats, and barley provide a good source of carbohydrates that
            fuel your workouts and help replenish glycogen stores in your
            muscles. Carbs are especially important for muscle gain because they
            provide the energy needed for intense workouts and muscle recovery.
          </li>
          <li>
            <strong>Hydrate for Performance:</strong> Staying hydrated is
            essential for optimal muscle function and recovery. Dehydration can
            lead to fatigue, reduced strength, and slower recovery. Aim to drink
            water throughout the day, and consider adding electrolyte-rich
            drinks if you're engaging in intense physical activity.
          </li>
          <li>
            <strong>Post-Workout Nutrition:</strong> Consuming a meal or snack
            containing both protein and carbohydrates within 30-60 minutes after
            your workout helps replenish glycogen stores and kickstart muscle
            recovery. This is a critical time for muscle repair, so ensure
            you're providing your body with the nutrients it needs.
          </li>
          <li>
            <strong>Supplements for Muscle Gain:</strong> While whole foods
            should be your primary source of nutrition, supplements such as
            protein powders, creatine, and branched-chain amino acids (BCAAs)
            can be helpful for meeting your calorie and protein needs. However,
            always consult with a healthcare professional before starting any
            new supplement regimen.
          </li>
          <li>
            <strong>Strength Training:</strong> Focus on weight training
            exercises that challenge all major muscle groups. Progressive
            overload is key, meaning you should aim to gradually increase the
            weight or resistance you use to continue building muscle. Don’t
            forget to allow your muscles time to recover by incorporating rest
            days into your workout routine.
          </li>
        </ul>
      </section>

      <section>
        <h3>2.3. Adjusting Calories for Your Goals</h3>
        <p>
          Adjusting your calorie intake to match your specific goals is
          essential for success in either weight loss or muscle gain. If you're
          not seeing the results you want, consider recalibrating your caloric
          intake based on your activity level, metabolic rate, and progress.
          Below are some tips for adjusting your diet to ensure you're on the
          right path:
        </p>
        <ul>
          <li>
            <strong>Assess Your Baseline:</strong> Start by calculating your
            Basal Metabolic Rate (BMR) and Total Daily Energy Expenditure
            (TDEE). This will help you understand how many calories you burn at
            rest and through physical activity. Use this as a reference point
            for adjusting your calorie intake.
          </li>
          <li>
            <strong>Increase Calories Gradually for Muscle Gain:</strong> If you
            find that you're not gaining muscle, start by adding 100-200 extra
            calories per day to your diet. Gradually increase your intake until
            you see improvements in muscle size and strength.
          </li>
          <li>
            <strong>Reduce Calories Slowly for Weight Loss:</strong> Aim for a
            slow and steady weight loss of 0.5-1 kg per week. To achieve this,
            reduce your daily calorie intake by 300-500 calories. A drastic
            reduction in calories can lead to muscle loss and metabolic
            slowdown.
          </li>
          <li>
            <strong>Be Patient:</strong> Whether you're trying to lose weight or
            gain muscle, it’s important to be patient and consistent. Body
            changes take time, and it’s essential to track your progress to
            ensure you’re heading in the right direction.
          </li>
        </ul>
      </section>

      <section>
        <h3>2.3. Tips for Calorie Tracking</h3>
        <p>
          Keeping track of your calorie intake is a vital tool for staying on
          track with your weight loss or muscle gain goals. It helps you
          understand your eating habits, make necessary adjustments, and stay
          accountable. Here are some tips to help you track your calories
          effectively and make the process easier:
        </p>
        <ul>
          <li>
            <strong>Use Apps:</strong> Calorie tracking apps are great tools to
            simplify the process of monitoring your calorie intake. Many apps
            have extensive food databases and allow you to scan barcodes to log
            packaged foods quickly. Popular apps such as MyFitnessPal,
            Cronometer, and Lose It! offer features like meal tracking, goal
            setting, and progress reports to make your tracking more efficient
            and insightful.
          </li>
          <li>
            <strong>Plan Ahead:</strong> One of the most effective ways to
            control your calorie intake is by planning your meals in advance.
            Meal prepping can help ensure that you're eating the right portions
            and staying within your calorie goals. You can use a weekly planner
            to portion out ingredients, create balanced meals, and avoid making
            spontaneous food choices that may be higher in calories than
            intended. By controlling your meals, you’re more likely to stay on
            track with your goals.
          </li>
          <li>
            <strong>Be Consistent:</strong> Logging your meals and snacks daily
            is crucial for accurate calorie tracking. Consistency is key to
            achieving your goals. Try to record your meals as soon as you eat
            them to prevent forgetting any snacks or drinks that might be
            contributing extra calories. This consistency will provide a clear
            picture of your habits, making it easier to adjust when needed.
          </li>
          <li>
            <strong>Weigh and Measure Your Food:</strong> For more accurate
            tracking, consider weighing or measuring your food. This ensures
            you’re logging the correct portion sizes. Using a food scale can
            prevent underestimating or overestimating the amount of food you’re
            consuming, leading to more precise calorie tracking.
          </li>
          <li>
            <strong>Be Aware of Hidden Calories:</strong> Don’t forget to
            account for beverages, oils, sauces, and condiments, which can add
            hidden calories. While a simple cup of coffee or a small amount of
            olive oil may seem insignificant, they can add up over time. Make
            sure to track everything you consume, even if it seems minimal.
          </li>
          <li>
            <strong>Set Realistic Goals:</strong> Use the calorie tracking apps
            to set realistic and achievable goals based on your weight loss or
            muscle gain objectives. Tracking allows you to break down your goals
            into smaller, manageable targets (e.g., daily calorie intake) that
            keep you motivated and on track. Adjust your goals if necessary to
            ensure steady progress.
          </li>
          <li>
            <strong>Stay Flexible:</strong> Calorie tracking doesn’t have to be
            rigid. Life can be unpredictable, and it's important to stay
            flexible. If you find yourself going over your calorie goal one day,
            don’t be discouraged. Just get back on track the next day.
            Flexibility helps prevent feelings of failure and promotes long-term
            adherence to your healthy eating habits.
          </li>
          <li>
            <strong>Track Your Macronutrients:</strong> While tracking calories
            is important, also consider tracking your macronutrients (carbs,
            proteins, and fats). This can provide more insight into your overall
            nutrition, ensuring that you’re getting the right balance of
            nutrients for your goals. For example, if you’re building muscle,
            you may want to track your protein intake to ensure you’re meeting
            your daily needs.
          </li>
          <li>
            <strong>Review Your Progress:</strong> Take the time to review your
            calorie logs regularly. This can help you identify any patterns or
            areas where you might be overeating or under-consuming. Reviewing
            your progress gives you an opportunity to make necessary adjustments
            before they derail your long-term goals.
          </li>
          <li>
            <strong>Be Honest With Yourself:</strong> The accuracy of your
            tracking depends on your honesty. If you miss logging a snack or
            underestimate a portion size, it can throw off your results. Try to
            be as honest and accurate as possible for the best outcomes. Over
            time, this will make tracking second nature and will help you
            achieve your goals.
          </li>
        </ul>
      </section>

      <section>
        <h3>2.4. Food Calorie Database</h3>
        <p>
          Explore a wide range of foods with detailed information on calorie
          content and serving sizes. Understanding the calorie content of
          different foods is key to managing your intake and achieving your
          fitness goals. Below are some common food categories and examples that
          provide useful nutritional information to help you make informed
          choices:
        </p>
        <ul>
          <li>
            <strong>Fruits and Vegetables:</strong> These foods are low in
            calories but high in essential vitamins, minerals, fiber, and
            antioxidants. They should form the basis of your diet, especially
            when aiming for weight loss. Examples include:
            <ul>
              <li>
                <strong>Apples:</strong> A medium apple (about 182g) contains
                approximately 95 calories, making it a healthy and satisfying
                snack.
              </li>
              <li>
                <strong>Berries:</strong> One cup of strawberries (about 150g)
                contains about 50 calories, while blueberries provide 84
                calories per cup. Both are high in antioxidants and fiber.
              </li>
              <li>
                <strong>Broccoli:</strong> One cup of cooked broccoli (about
                156g) contains around 55 calories and is rich in vitamin C and
                fiber.
              </li>
              <li>
                <strong>Spinach:</strong> One cup of cooked spinach (about 180g)
                has just 40 calories and is loaded with iron and other important
                nutrients.
              </li>
            </ul>
          </li>
          <li>
            <strong>Proteins:</strong> Protein is crucial for muscle repair and
            growth, and it also helps keep you feeling full. Here are some
            excellent sources of lean protein:
            <ul>
              <li>
                <strong>Chicken:</strong> A 3-ounce serving of skinless chicken
                breast provides about 140 calories and 26g of protein.
              </li>
              <li>
                <strong>Fish:</strong> Salmon (3 ounces, cooked) contains about
                200 calories and 22g of protein, along with heart-healthy
                omega-3 fatty acids.
              </li>
              <li>
                <strong>Eggs:</strong> One large egg contains about 70 calories
                and 6g of protein. They are an excellent source of vitamins and
                minerals.
              </li>
              <li>
                <strong>Tofu:</strong> A 3-ounce serving of firm tofu contains
                about 70 calories and 8g of protein. It’s a great plant-based
                option for vegetarians.
              </li>
              <li>
                <strong>Legumes:</strong> A half-cup of cooked lentils contains
                approximately 115 calories and 9g of protein, along with fiber,
                making them a filling option.
              </li>
            </ul>
          </li>
          <li>
            <strong>Carbohydrates:</strong> Carbohydrates provide essential
            energy, especially for those who exercise regularly. Choose whole
            grains and starchy vegetables for a more nutritious option:
            <ul>
              <li>
                <strong>Whole Grains:</strong> One cup of cooked quinoa contains
                about 222 calories and provides 8g of protein and 5g of fiber.
              </li>
              <li>
                <strong>Oats:</strong> A half-cup of rolled oats contains
                approximately 150 calories and is a great source of fiber, which
                aids in digestion.
              </li>
              <li>
                <strong>Sweet Potatoes:</strong> One medium baked sweet potato
                (about 150g) contains around 103 calories and is packed with
                vitamins A and C.
              </li>
            </ul>
          </li>
          <li>
            <strong>Fats:</strong> Healthy fats are important for hormone
            production, brain health, and satiety. Here are some nutritious
            sources of fats:
            <ul>
              <li>
                <strong>Avocados:</strong> One medium avocado contains about 240
                calories and provides heart-healthy monounsaturated fats.
              </li>
              <li>
                <strong>Nuts:</strong> A quarter-cup of almonds (about 23 nuts)
                contains approximately 200 calories and is rich in healthy fats
                and protein.
              </li>
              <li>
                <strong>Seeds:</strong> One tablespoon of chia seeds contains
                about 58 calories and is a good source of omega-3 fatty acids
                and fiber.
              </li>
              <li>
                <strong>Olive Oil:</strong> One tablespoon of olive oil provides
                about 120 calories and is rich in monounsaturated fats, perfect
                for salads or cooking.
              </li>
            </ul>
          </li>
        </ul>
      </section>

      <section>
        <h2>Understanding Calories and Their Role in Your Body</h2>
        <p>
          Calories are a measure of energy that your body requires to perform
          essential functions. This includes not only physical activities but
          also involuntary processes like breathing, circulation, and digestion.
        </p>

        <h3>The Science Behind Calories</h3>
        <p>
          A calorie is technically the amount of energy needed to raise the
          temperature of one gram of water by one degree Celsius. When applied
          to nutrition, this measure becomes critical as it translates into how
          your body generates and utilizes energy.
        </p>

        <h3>Basal Metabolic Rate (BMR)</h3>
        <p>
          Your BMR is the number of calories your body needs to maintain
          essential functions at rest. This includes keeping your heart beating,
          lungs breathing, and brain functioning. Factors that influence BMR
          include:
        </p>
        <ul>
          <li>
            <strong>Age:</strong> Metabolism tends to slow down with age.
          </li>
          <li>
            <strong>Gender:</strong> Men typically have higher BMR due to
            greater muscle mass.
          </li>
          <li>
            <strong>Body Composition:</strong> Muscle burns more calories at
            rest than fat.
          </li>
          <li>
            <strong>Genetics:</strong> Some people naturally have a faster
            metabolism.
          </li>
        </ul>

        <h3>Total Daily Energy Expenditure (TDEE)</h3>
        <p>
          TDEE is the total number of calories you burn in a day, including BMR
          and calories burned through physical activity and digestion.
          Calculating TDEE is essential for determining how many calories you
          need to maintain, lose, or gain weight.
        </p>
        <ul>
          <li>
            <strong>Activity Thermogenesis:</strong> This includes both
            structured exercise and non-exercise activities like walking or
            fidgeting.
          </li>
          <li>
            <strong>Thermic Effect of Food (TEF):</strong> Digesting and
            processing food consumes about 10% of your daily calorie intake.
          </li>
        </ul>
      </section>

      <section>
        <h2>Calories for Specific Goals</h2>

        <h3>Weight Maintenance</h3>
        <p>
          Maintaining your current weight requires consuming calories equivalent
          to your Total Daily Energy Expenditure (TDEE). This ensures your
          energy input matches your output, keeping your weight stable.
          Regularly monitor your weight and adjust your calorie intake based on
          changes in activity levels or metabolism.
        </p>

        <h3>Calories for Weight Loss</h3>
        <p>
          A safe and effective approach to weight loss involves creating a
          calorie deficit of 500-750 calories per day. This can lead to a weight
          loss of approximately 0.5-1 kg (1-2 pounds) per week, which is
          considered sustainable and healthy. The key is to reduce calories
          without sacrificing essential nutrients.
        </p>
        <ul>
          <li>
            <strong>Prioritize High-Protein Foods:</strong> Protein plays a
            crucial role in preserving muscle mass during weight loss, improving
            satiety, and boosting metabolism. Include lean meats, fish, eggs,
            dairy, tofu, and legumes in your diet.
          </li>
          <li>
            <strong>Limit Processed Foods:</strong> Processed foods are often
            high in calories, sugar, and unhealthy fats while being low in
            nutrients. Opt for whole, minimally processed foods to maximize the
            nutritional value of your meals.
          </li>
          <li>
            <strong>Incorporate High-Fiber Foods:</strong> Fiber-rich foods such
            as vegetables, fruits, whole grains, and legumes can help you feel
            full for longer periods, reducing the temptation to overeat.
          </li>
          <li>
            <strong>Stay Hydrated:</strong> Sometimes thirst is mistaken for
            hunger. Drinking water before meals can also help control portion
            sizes.
          </li>
        </ul>

        <h3>Calories for Muscle Gain</h3>
        <p>
          Building muscle requires a calorie surplus, typically 250-500 calories
          above your maintenance level. This surplus provides the energy needed
          for muscle repair and growth. However, gaining muscle effectively also
          requires a strategic approach to nutrition and exercise.
        </p>
        <ul>
          <li>
            <strong>Progressive Overload:</strong> Strength training with
            progressively heavier weights stimulates muscle hypertrophy. Pairing
            this with increased calorie intake ensures your body has the
            resources to build muscle.
          </li>
          <li>
            <strong>Macronutrient Balance:</strong> To support muscle growth,
            aim for a well-balanced diet with:
            <ul>
              <li>
                <strong>Protein:</strong> 1.6-2.2 g per kg of body weight daily
                to repair and build muscle.
              </li>
              <li>
                <strong>Carbohydrates:</strong> Provide the primary energy
                source for workouts. Focus on complex carbs like oats, brown
                rice, and sweet potatoes.
              </li>
              <li>
                <strong>Healthy Fats:</strong> Include sources like avocados,
                nuts, seeds, and olive oil for essential fatty acids and
                hormonal balance.
              </li>
            </ul>
          </li>
          <li>
            <strong>Pre- and Post-Workout Nutrition:</strong> To optimize
            performance and recovery:
            <ul>
              <li>
                <strong>Pre-Workout:</strong> A meal rich in carbs and moderate
                in protein 1-2 hours before exercise can enhance energy levels.
              </li>
              <li>
                <strong>Post-Workout:</strong> Consuming protein (20-30g) and
                carbs within 30-60 minutes after a workout helps replenish
                glycogen stores and initiates muscle repair.
              </li>
            </ul>
          </li>
          <li>
            <strong>Track Your Progress:</strong> Monitor your weight, body
            composition, and strength improvements. Adjust your calorie intake
            if you're gaining excessive fat or not seeing desired muscle growth.
          </li>
        </ul>
      </section>

      <section>
        <h2>The Role of Macronutrients</h2>

        <h3>Carbohydrates</h3>
        <p>
          Carbs are the body’s primary energy source. Focus on complex
          carbohydrates like whole grains, fruits, and vegetables, which provide
          sustained energy and essential nutrients.
        </p>

        <h3>Proteins</h3>
        <p>
          Proteins are crucial for muscle repair, immune function, and enzyme
          production. Include a variety of protein sources such as lean meats,
          fish, eggs, dairy, tofu, and legumes.
        </p>

        <h3>Fats</h3>
        <p>
          Healthy fats support hormone production, brain health, and energy
          storage. Prioritize unsaturated fats from sources like avocados, nuts,
          seeds, and olive oil, while limiting saturated and trans fats.
        </p>
      </section>

      <section>
        <h2>Advanced Topics in Calorie Management</h2>

        <h3>Intermittent Fasting</h3>
        <p>
          Intermittent fasting (IF) is a dietary approach that cycles between
          periods of eating and fasting. Common methods include:
        </p>
        <ul>
          <li>
            <strong>16/8 Method:</strong> Fast for 16 hours and eat within an
            8-hour window.
          </li>
          <li>
            <strong>5:2 Diet:</strong> Consume a regular diet for 5 days and
            limit intake to 500-600 calories on 2 non-consecutive days.
          </li>
        </ul>

        <h3>Calorie Cycling</h3>
        <p>
          Calorie cycling involves alternating calorie intake on different days
          to match activity levels. For example:
        </p>
        <ul>
          <li>High-calorie days on intense workout days.</li>
          <li>Low-calorie days on rest or light activity days.</li>
        </ul>

        <h3>Impact of Sleep on Calorie Metabolism</h3>
        <p>
          Poor sleep quality can disrupt hunger hormones like ghrelin and
          leptin, leading to increased appetite and cravings. Aim for 7-9 hours
          of sleep per night to support optimal calorie regulation.
        </p>

        <h3>Caloric Density</h3>
        <p>
          Understanding caloric density helps you make smarter food choices.
          Foods low in caloric density (e.g., vegetables) provide more volume
          and nutrients per calorie compared to calorie-dense foods (e.g.,
          oils).
        </p>
      </section>

      <section>
        <h2>Food Calorie Database</h2>
        <p>
          Explore the comprehensive food calorie database to make informed
          dietary choices and achieve your health goals:
        </p>
      </section>
      <FoodCalorieDatabase />
    </div>
  );
}
